import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MdSpeed } from "react-icons/md";
import { BiGasPump } from "react-icons/bi";
import { FaRegCalendar, FaSearch } from "react-icons/fa";
import { FaGear } from "react-icons/fa6";
import { FiEye } from "react-icons/fi";
import { formatMoney } from "../utils/formatMoney";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FiltrosAvancados from "../components/FiltrosAvancados";
import { firestore } from "../firebase-config";
import { id_firebase } from "../content-site";
import { collection, doc, query, where, orderBy, onSnapshot } from "firebase/firestore";

const FILTROS_VAZIOS = {
    tipo: "",
    cor: "",
    portas: "",
    kmMax: "",
    precoMin: "",
    precoMax: ""
};

const normalizarTexto = (valor) => String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export default function Estoque() {
    const [veiculos, setVeiculos] = useState([]);
    const [busca, setBusca] = useState("");
    const [filtrosAvancados, setFiltrosAvancados] = useState(FILTROS_VAZIOS);
    const [filtrosAplicados, setFiltrosAplicados] = useState({ busca: "", ...FILTROS_VAZIOS });
    const [ordenarPor, setOrdenarPor] = useState("relevancia");
    const [paginaAtual, setPaginaAtual] = useState(1);
    const veiculosPorPagina = 9;
    const location = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);

        const q = query(
            collection(doc(collection(firestore, "revenda"), id_firebase), "veiculos"),
            where("veremovido", "==", 0),
            where("vevendido", "==", 0),
            orderBy("updated_at", "desc")
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const items = [];
            querySnapshot.forEach((doc) => {
                const document = doc.data();
                document.id = doc.id;
                if (document.veexibir_site !== false) {
                    items.push(document);
                }
            });
            setVeiculos(items);
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        // Filtros principais
        const marca = params.get("vefipe_marca") || "";
        const modelo = params.get("vefipe_name") || "";
        const ano = params.get("vefipe_ano_modelo") || "";

        // Filtros avançados
        const tipo = params.get("tipo") || "";
        const cor = params.get("cor") || "";
        const portas = params.get("portas") || "";
        const kmMax = params.get("kmMax") || "";
        const precoMin = params.get("precoMin") || "";
        const precoMax = params.get("precoMax") || "";

        const buscaInicial = [marca, modelo, ano].filter(Boolean).join(" ");
        setBusca(buscaInicial);
        setFiltrosAvancados({ tipo, cor, portas, kmMax, precoMin, precoMax });
        setFiltrosAplicados({ busca: buscaInicial, tipo, cor, portas, kmMax, precoMin, precoMax });
    }, [location.search]);

    const handleBuscar = (event) => {
        event?.preventDefault();
        setFiltrosAplicados({ busca: busca.trim(), ...filtrosAvancados });
    };

    const handleLimparBusca = () => {
        setBusca("");
        setFiltrosAvancados(FILTROS_VAZIOS);
        setFiltrosAplicados({ busca: "", ...FILTROS_VAZIOS });
    };

    const veiculosFiltrados = veiculos.filter(v => {
        const termosBusca = normalizarTexto(filtrosAplicados.busca).split(/\s+/).filter(Boolean);
        const dadosPesquisaveis = normalizarTexto(
            `${v.vefipe_marca ?? ""} ${v.vefipe_name ?? ""} ${v.veano_fabricacao ?? ""} ${v.vefipe_ano_modelo ?? ""}`
        );

        return (
            termosBusca.every(termo => dadosPesquisaveis.includes(termo)) &&
            (!filtrosAplicados.tipo || String(v.vecarroceria) === filtrosAplicados.tipo) &&
            (!filtrosAplicados.cor || String(v.vecor) === filtrosAplicados.cor) &&
            (!filtrosAplicados.portas || String(v.veportas) === filtrosAplicados.portas) &&
            (!filtrosAplicados.kmMax || v.vequilometragem <= Number(filtrosAplicados.kmMax)) &&
            (!filtrosAplicados.precoMin || v.vevalor >= Number(filtrosAplicados.precoMin)) &&
            (!filtrosAplicados.precoMax || v.vevalor <= Number(filtrosAplicados.precoMax))
        );
    });

    const ordenarVeiculos = (lista) => {
        switch (ordenarPor) {
            case "menor_preco": return [...lista].sort((a, b) => a.vevalor - b.vevalor);
            case "maior_preco": return [...lista].sort((a, b) => b.vevalor - a.vevalor);
            case "menor_km": return [...lista].sort((a, b) => a.vequilometragem - b.vequilometragem);
            case "marca_az": return [...lista].sort((a, b) => a.vefipe_marca.localeCompare(b.vefipe_marca));
            default: return lista;
        }
    };

    const veiculosFiltradosOrdenados = ordenarVeiculos(veiculosFiltrados);
    const totalPaginas = Math.ceil(veiculosFiltradosOrdenados.length / veiculosPorPagina);
    const indiceInicial = (paginaAtual - 1) * veiculosPorPagina;
    const indiceFinal = indiceInicial + veiculosPorPagina;
    const veiculosPaginados = veiculosFiltradosOrdenados.slice(indiceInicial, indiceFinal);

    useEffect(() => {
        setPaginaAtual(1);
    }, [filtrosAplicados, ordenarPor]);

    function separarNomeVeiculo(nomeCompleto) {
        if (!nomeCompleto) return { mainName: '', subName: '' };

        const palavras = nomeCompleto.split(' ');

        const STOP_WORDS = [
            'CD', 'SR', 'SRV', 'GR', 'XEI', 'LTZ', 'LX',
            '4X4', '4X2',
            '1.0', '1.2', '1.3', '1.4', '1.5', '1.8', '1.6', '2.0', '2.8',
            'TDI', 'TURBO', 'DIESEL', 'FLEX',
            'AUT', 'AUT.', 'MT', 'CVT'
        ];

        const indexSpec = palavras.findIndex(p =>
            STOP_WORDS.includes(p)
        );

        if (indexSpec === -1) {
            return {
                mainName: palavras.slice(0, 2).join(' '),
                subName: palavras.slice(2).join(' ')
            };
        }

        return {
            mainName: palavras.slice(0, indexSpec).join(' '),
            subName: palavras.slice(indexSpec).join(' ')
        };
    }
    return (
        <div>
            <Header />

            {/* Conteúdo semelhante ao Next.js, removido uso de Image do next/image */}
            <div className="relative h-[350px] w-full">
                <img
                    src="/bannerA.jpeg"
                    alt="Banner do A2 Motors Multimarcas"
                    className="object-cover w-full h-full"
                />
                <div className="absolute inset-0 bg-black/50" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4 z-10 mt-24">
                    <h1 className="text-6xl font-bold">Nosso Estoque</h1>
                    <p className="text-lg font-semibold mt-2">Explore nossa seleção completa de veículos premium</p>
                </div>
            </div>
            <div className="flex justify-center items-center w-full mt-10 mb-8">
                <div className="bg-white rounded-xl flex flex-col justify-center gap-2 md:gap-4 p-6 shadow-lg w-full max-w-3xl">
                    <h2 className="font-bold text-2xl text-center text-neutral-800">Encontre seu veículo ideal</h2>
                    <form className="flex flex-col md:flex-row gap-2 md:gap-4 items-center" onSubmit={handleBuscar}>
                        <div className="relative w-full flex-1">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                            <input
                                className="p-2 pl-10 rounded-md border border-gray-300 text-black w-full"
                                type="search"
                                value={busca}
                                onChange={e => setBusca(e.target.value)}
                                placeholder="Digite a marca, o modelo ou o ano"
                                aria-label="Pesquisar veículo por marca, modelo ou ano"
                            />
                        </div>

                        {(busca || filtrosAplicados.busca || Object.values(filtrosAvancados).some(Boolean)) && (
                            <button
                                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md font-semibold whitespace-nowrap"
                                type="button"
                                onClick={handleLimparBusca}
                            >
                                Limpar filtros
                            </button>
                        )}

                        <button
                            className="flex items-center gap-2 bg-yellow-600 text-white px-4 py-2 rounded-md w-full md:w-auto"
                            type="submit"
                        >
                            <FaSearch /> Buscar
                        </button>
                    </form>
                    <div className="flex justify-center mt-4">
                        <div>
                            <FiltrosAvancados
                                filtros={filtrosAvancados}
                                setFiltros={setFiltrosAvancados}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center mt-4 text-sm text-gray-700">
                <span>
                    Mostrando {veiculosFiltradosOrdenados.length > 0 ? `${indiceInicial + 1}-${Math.min(indiceFinal, veiculosFiltradosOrdenados.length)}` : "0"} de {veiculosFiltradosOrdenados.length} veículos
                </span>

                <div className="flex items-center mt-2 md:mt-0 gap-2">
                    <span className="font-medium">Ordenar por:</span>
                    <select
                        className="border border-gray-300 rounded-md p-2"
                        value={ordenarPor}
                        onChange={(e) => setOrdenarPor(e.target.value)}
                    >
                        <option value="relevancia">Relevância</option>
                        <option value="menor_preco">Menor preço</option>
                        <option value="maior_preco">Maior preço</option>
                        <option value="menor_km">Menor KM</option>
                        <option value="marca_az">Marca A-Z</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6 px-4 max-w-6xl mx-auto">
                {veiculosPaginados.length === 0 && (
                    <div className="col-span-full text-center text-gray-500 py-12">
                        Nenhum veículo encontrado com os filtros selecionados.
                    </div>
                )}
                {veiculosPaginados.map((veiculo) => {
                    const { mainName, subName } = separarNomeVeiculo(veiculo.vefipe_name);

                    const cambio = veiculo.vecambio === 'A' ? 'Automático' : veiculo.vecambio === 'M' ? 'Manual' : veiculo.vecambio;

                    veiculo.vecambio = cambio;

                    return (
                        <div key={veiculo.id} className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col h-full">
                            <img className="object-contain w-full h-40 md:h-56 lg:h-64" src={veiculo.imagens?.[0]?.fileUrl} alt={veiculo.titulo} />

                            <div className="p-3 flex-1 flex flex-col justify-between">
                                <div>
                                    <h3 className="text-base font-bold uppercase">
                                        {veiculo.vefipe_marca} {mainName}
                                    </h3>

                                    {subName && (
                                        <p className="text-sm text-gray-400 uppercase">
                                            {subName}
                                        </p>
                                    )}
                                </div>

                                <div className="grid grid-cols-2 text-gray-500 space-y-2 text-sm">
                                    <span className="flex items-center gap-1">
                                        <MdSpeed /> {veiculo.vequilometragem}km
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <BiGasPump /> {veiculo.vefipe_combustivel}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <FaRegCalendar /> {veiculo.veano_fabricacao} / {veiculo.vefipe_ano_modelo}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <FaGear /> {veiculo.vecambio}
                                    </span>
                                </div>

                                <hr className="text-gray-600 mt-2 mb-2" />
                                <div className="mt-2 flex gap-3 items-center justify-between">
                                    <p className="text-lg font-bold text-yellow-600">
                                        {veiculo.vevalor ? formatMoney(veiculo.vevalor) : 'Consulte o preço'}
                                    </p>

                                    <a href={`/veiculo/${veiculo.id}`} className="bg-yellow-600 text-white px-3 py-1.5 rounded flex gap-2 items-center text-sm">
                                        <FiEye /> Ver Detalhes
                                    </a>

                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {totalPaginas > 1 && (
                <div className="flex justify-center items-center gap-2 mt-8 mb-12">
                    <button
                        className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-50"
                        onClick={() => setPaginaAtual(p => Math.max(1, p - 1))}
                        disabled={paginaAtual === 1}
                    >
                        Anterior
                    </button>
                    {[paginaAtual, paginaAtual + 1]
                        .filter(p => p <= totalPaginas)
                        .map(p => (
                            <button
                                key={p}
                                className={`px-3 py-1 rounded border border-gray-300 ${paginaAtual === p ? "bg-yellow-600 text-white font-bold" : "bg-white"}`}
                                onClick={() => setPaginaAtual(p)}
                            >
                                {p}
                            </button>
                        ))}
                    {paginaAtual + 1 < totalPaginas && (
                        <button
                            className="px-3 py-1 rounded border border-gray-300 bg-white"
                            onClick={() => setPaginaAtual(paginaAtual + 2)}
                        >
                            {paginaAtual + 2}
                        </button>
                    )}
                    <button
                        className="px-3 py-1 rounded border border-gray-300 bg-white disabled:opacity-50"
                        onClick={() => setPaginaAtual(p => Math.min(totalPaginas, p + 1))}
                        disabled={paginaAtual === totalPaginas}
                    >
                        Próxima
                    </button>
                </div>
            )}

            <Footer />
        </div>
    );
}
